/**
 * announceToScreenReader
 *
 * Writes a message to a persistent `role="alert"` element on document.body
 * via direct DOM manipulation (no React state). This bypasses React batching
 * and is the only reliable approach with VoiceOver + Safari/Chrome on macOS.
 *
 * Pattern used by @react-aria/live-announcer (Adobe) and axe-core.
 */

let assertiveAnnouncer: HTMLElement | null = null;
let politeAnnouncer: HTMLElement | null = null;

function createAnnouncer(isAssertive: boolean): HTMLElement {
  const el = document.createElement('div');
  el.setAttribute('role', isAssertive ? 'alert' : 'status');
  el.setAttribute('aria-live', isAssertive ? 'assertive' : 'polite');
  el.setAttribute('aria-atomic', 'true');

  // Visually hidden — accessible to AT, invisible to sighted users
  Object.assign(el.style, {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: '0',
  });

  document.body.appendChild(el);
  return el;
}

// Initialize immediately on module load — VoiceOver must observe the element
// existing in the DOM *before* we write to it, otherwise it ignores mutations.
if (typeof document !== 'undefined') {
  assertiveAnnouncer = createAnnouncer(true);
  politeAnnouncer = createAnnouncer(false);
}

/**
 * Delay (ms) before writing to the polite announcer after DOM focus moves (e.g. card
 * region focus after flip). Default 150ms is often too fast for NVDA to surface status
 * text after a programmatic focus change (QUIZ-459).
 */
export const ANNOUNCE_AFTER_FOCUS_MS = 450;

/**
 * Announce a message to screen readers.
 * Clears the element first so repeated identical messages are re-announced.
 *
 * @param message     - The text to announce.
 * @param isAssertive - Whether to use the assertive (alert) or polite (status) region.
 * @param delay       - ms to wait before writing (default 150). Gives VoiceOver
 *                      time to settle focus changes before the announcement fires.
 */
export function announceToScreenReader(message: string, isAssertive = true, delay = 150): void {
  const announcer = isAssertive ? assertiveAnnouncer : politeAnnouncer;
  if (!announcer) return;

  announcer.textContent = '';
  setTimeout(() => {
    if (announcer) announcer.textContent = message;
  }, delay);
}
